import { types as sdkTypes } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/StoreListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/StoreListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/StoreListingsPage/FETCH_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  userId: null,
  listingRefs: [],
  totalItems: 0,
  pagination: null,
  fetchListingsInProgress: false,
  fetchListingsError: null,
};

export default function storeListingsPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        userId: payload.userId,
        fetchListingsInProgress: true,
        fetchListingsError: null,
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        listingRefs: payload.listingRefs,
        totalItems: payload.totalItems,
        pagination: payload.pagination,
        fetchListingsInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      return {
        ...state,
        listingRefs: [],
        fetchListingsInProgress: false,
        fetchListingsError: payload,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchListingsRequest = userId => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { userId },
});

export const fetchListingsSuccess = ({ listingRefs, totalItems, pagination }) => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { listingRefs, totalItems, pagination },
});

export const fetchListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const loadData = (params = {}, search = '', _config) => (dispatch, getState, sdk) => {
  const { userId } = params;

  if (!userId) {
    const error = new Error('StoreListingsPage requires a userId param');
    return Promise.reject(error);
  }

  const queryParams = new URLSearchParams(search);
  const page = Math.max(1, parseInt(queryParams.get('page'), 10) || 1);

  const userUUID = new UUID(userId);
  dispatch(fetchListingsRequest(userUUID));

  // Fetch the store user in parallel so we always have the store name
  // for the page title and schema, even when the store has zero listings.
  const userPromise = sdk.users
    .show({
      id: userId,
      // include: ['profileImage'],
      // 'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return response;
    });

  const listingsPromise = sdk.listings
    .query({
      authorId: userUUID,
      'fields.listing': ['title', 'publicData'],
      include: ['images'],
      'fields.image': ['variants.listing-card', 'variants.listing-card-2x'],
      page,
      perPage: 20,
    })
    .then(response => {
      const listingRefs = response.data.data.map(({ id, type }) => ({ id, type }));
      const { totalItems, totalPages, page: responsePage, perPage } = response.data.meta;

      dispatch(addMarketplaceEntities(response));
      dispatch(
        fetchListingsSuccess({
          listingRefs,
          totalItems,
          pagination: { totalItems, totalPages, page: responsePage, perPage },
        })
      );
      return response;
    });

  return Promise.all([userPromise, listingsPromise]).catch(e => {
    dispatch(fetchListingsError(storableError(e)));
  });
};
